import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import OnboardingWizard from "@/components/OnboardingWizard";
import VaultUnlock from "@/components/VaultUnlock";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, GraduationCap, UsersThree, Lightning, FolderLock, Kanban, WarningCircle, Sparkle, Crown } from "@phosphor-icons/react";

const BILLING_ENABLED = process.env.REACT_APP_BILLING_ENABLED === "true";

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ work: 0, edu: 0, refs: 0, skills: 0, docs: 0, apps: 0 });
  const [apps, setApps] = useState([]);
  const [expiry, setExpiry] = useState({ expired: [], expiring_soon: [], counts: { expired: 0, expiring_soon: 0, total: 0 } });
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    (async () => {
      const [w, e, r, sk, d, ap, ex] = await Promise.all([
        api.get("/work"), api.get("/education"), api.get("/references"), api.get("/skills"),
        api.get("/documents"), api.get("/applications"), api.get("/documents/expiry/status"),
      ]);
      setStats({ work: w.data.length, edu: e.data.length, refs: r.data.length, skills: sk.data.length, docs: d.data.length, apps: ap.data.length });
      setApps(ap.data.slice(0, 5));
      setExpiry(ex.data);
      const onboarded = (() => { try { return localStorage.getItem("vault_onboarded") === "1"; } catch { return false; } })();
      const isEmpty = w.data.length === 0 && e.data.length === 0;
      if (!onboarded && isEmpty) setShowOnboarding(true);
    })();
    // mount-only: `api` is a stable module singleton, setters are stable, no reactive deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // All tiles share a clean white "vault drawer" body. Stripes carry a thin
  // brand accent (cobalt or sage) — toned right down from the previous bright
  // pastel rainbow that felt random.
  const tiles = [
    { to: "/vault",     label: "work experience", count: stats.work,   icon: Briefcase,     stripe: "var(--brand-primary)" },
    { to: "/vault",     label: "education",       count: stats.edu,    icon: GraduationCap, stripe: "var(--bolt-sage)"      },
    { to: "/vault",     label: "references",      count: stats.refs,   icon: UsersThree,    stripe: "var(--brand-primary)" },
    { to: "/vault",     label: "skills",          count: stats.skills, icon: Lightning,     stripe: "var(--bolt-sage)"      },
    { to: "/documents", label: "document safe",   count: stats.docs,   icon: FolderLock,    stripe: "var(--brand-primary)" },
    { to: "/tracker",   label: "applications",    count: stats.apps,   icon: Kanban,        stripe: "var(--bolt-sage)"      },
  ];

  return (
    <AppShell title="Dashboard">
      <VaultUnlock/>
      {showOnboarding && <OnboardingWizard onClose={() => setShowOnboarding(false)}/>}
      <p className="text-[var(--brand-muted)] mb-8 max-w-2xl">
        your vault, at a glance. add stuff once. reuse forever. apply faster than your nervous system can object.
      </p>

      {BILLING_ENABLED && user?.tier === "free" && (
        <Link to="/billing" data-testid="dashboard-upgrade-card"
          className="glass-card p-5 mb-6 flex items-center justify-between hover:-translate-y-0.5 transition-transform group block">
          <div className="flex items-center gap-4 relative z-10">
            <div className="glass-pill rounded-md w-12 h-12 flex items-center justify-center shrink-0">
              <Sparkle size={22} weight="fill" className="text-[var(--brand-primary)]"/>
            </div>
            <div>
              <p className="label-tiny mb-0.5">/upgrade</p>
              <p className="font-display text-lg font-semibold tracking-tight leading-tight">
                you&apos;re on the free plan. <span className="text-[var(--brand-muted)]">paid tiers from £3.99/mo (annual) unlock more docs, deeper Vault Coach access, and a public share link.</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-sm font-medium shrink-0 ml-3 relative z-10">
            see plans <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform"/>
          </div>
        </Link>
      )}

      {(expiry.counts.expired > 0 || expiry.counts.expiring_soon > 0) && (
        <Link to="/documents" data-testid="dashboard-expiry-alert"
          className="card-hard rounded-md p-5 bg-white mb-6 flex items-center justify-between hover:-translate-y-0.5 transition-transform relative overflow-hidden">
          <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-1.5 bg-[var(--brand-primary)]"/>
          <div className="flex items-center gap-3">
            <WarningCircle size={24} weight="fill"/>
            <div>
              <p className="font-medium">
                {expiry.counts.expired > 0 && <>{expiry.counts.expired} expired</>}
                {expiry.counts.expired > 0 && expiry.counts.expiring_soon > 0 && " · "}
                {expiry.counts.expiring_soon > 0 && <>{expiry.counts.expiring_soon} expiring soon</>}
              </p>
              <p className="text-xs text-[var(--brand-muted)] font-mono-d">tap to review documents</p>
            </div>
          </div>
          <ArrowRight size={18} weight="bold"/>
        </Link>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {tiles.map((t, i) => {
          const Ic = t.icon;
          return (
            <Link key={t.label} to={t.to} data-testid={`tile-${t.label.replace(/\s/g, "-")}`}
              className="card-hard rounded-md p-6 bg-white tile-pop relative overflow-hidden"
              style={{ animationDelay: `${i * 60}ms` }}>
              <span aria-hidden="true"
                className="absolute left-0 top-0 bottom-0 w-1.5"
                style={{ background: t.stripe }}/>
              <div className="flex items-start justify-between">
                <Ic size={28} weight="duotone" />
                <span className="font-display text-4xl font-semibold tracking-tight" data-testid={`count-${t.label.replace(/\s/g, "-")}`}>{t.count}</span>
              </div>
              <p className="label-tiny mt-6">{t.label}</p>
              <div className="mt-3 flex items-center gap-1 text-sm font-medium">manage <ArrowRight size={14} weight="bold"/></div>
            </Link>
          );
        })}
      </div>

      <div className="mt-10 card-hard rounded-md p-6 bg-white">
        <div className="flex items-center justify-between mb-4">
          <p className="label-tiny">/recent applications</p>
          <Link to="/tracker" className="text-sm underline">go to tracker →</Link>
        </div>
        {apps.length === 0 ? (
          <p className="text-[var(--brand-muted)] text-sm">no applications yet. when you log one, it lands here.</p>
        ) : (
          <ul className="divide-y">
            {apps.map((a) => {
              const isDemo = (a.notes || "").trim().toLowerCase().startsWith("demo ·");
              return (
                <li key={a.id} className="py-3 flex items-center justify-between gap-3" data-testid={`recent-app-${a.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium flex items-center gap-2 flex-wrap">
                      <span>{a.role} <span className="text-[var(--brand-muted)]">· {a.company}</span></span>
                      {isDemo && (
                        <span data-testid={`recent-demo-tag-${a.id}`}
                          className="inline-flex items-center px-1.5 py-0.5 rounded-full border-[1.5px] border-[var(--brand-ink)] bg-[var(--bolt-mustard)] font-mono-d text-[9px] tracking-[0.15em] uppercase">
                          example
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[var(--brand-muted)] font-mono-d uppercase">{a.status}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
